import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, Upload, Trash2, Image as ImageIcon,
  Radio, Handshake, Film,
} from "lucide-react";

// Frozen label sets — mirrors the backend allowlist in
// routers/callsheet_branding.py so validation matches.
const SLOT2_LABELS = [
  "Co-Producer", "Line Production", "Studio",
  "Channel", "Agency", "Event Co",
];
const SLOT3_LABELS = ["Client", "Partner"];

// One reusable slot card — used for both org-level and project-level
// slots. Renders an image preview / placeholder + upload / clear
// buttons and (optionally) a label picker.
function LogoSlot({
  title, subtitle, ref: logoRef, previewSrc, labelOptions, labelValue,
  onLabelChange, onFilePicked, onClear, disabled, uploading,
  testidBase,
}) {
  const inputRef = useRef(null);
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex flex-col gap-3"
         data-testid={`${testidBase}-card`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/50">{title}</div>
          {subtitle && <div className="text-xs text-white/60 mt-1">{subtitle}</div>}
        </div>
        {labelOptions && (
          <select
            value={labelValue || labelOptions[0]}
            onChange={(e) => onLabelChange(e.target.value)}
            disabled={disabled}
            className="text-[10px] tracking-[0.22em] uppercase bg-black/40 text-white border border-white/15 rounded-full px-3 py-1.5 focus:outline-none focus:border-white/40"
            data-testid={`${testidBase}-label`}>
            {labelOptions.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        )}
      </div>

      <div className="aspect-[3/1.5] rounded-xl bg-black/40 border border-dashed border-white/12 flex items-center justify-center overflow-hidden">
        {previewSrc ? (
          <img src={previewSrc} alt="Logo preview" className="max-h-full max-w-full object-contain" />
        ) : logoRef ? (
          <div className="text-[10px] text-white/40">Loading…</div>
        ) : (
          <div className="text-center px-4">
            <ImageIcon size={20} className="text-white/25 mx-auto mb-1.5" />
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/40">
              No logo yet · placeholder will be shown in PDF
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <input ref={inputRef} type="file"
               accept="image/png,image/jpeg,image/webp,image/svg+xml"
               className="hidden"
               onChange={(e) => {
                 const f = e.target.files?.[0];
                 if (f) onFilePicked(f);
                 e.target.value = "";
               }}
               data-testid={`${testidBase}-input`} />
        <button type="button" disabled={disabled || uploading}
                onClick={() => inputRef.current?.click()}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-white/15 hover:border-white/35 text-[10px] tracking-[0.22em] uppercase text-white/80 hover:text-white px-3 py-2 disabled:opacity-40 transition-colors"
                data-testid={`${testidBase}-upload`}>
          <Upload size={11} /> {logoRef ? "Replace" : "Upload"}
        </button>
        {logoRef && (
          <button type="button" disabled={disabled}
                  onClick={onClear}
                  className="inline-flex items-center justify-center gap-1.5 rounded-full border border-red-500/25 hover:border-red-400/55 text-[10px] tracking-[0.22em] uppercase text-red-300/90 hover:text-red-200 px-3 py-2 disabled:opacity-40 transition-colors"
                  data-testid={`${testidBase}-clear`}>
            <Trash2 size={11} />
          </button>
        )}
      </div>
    </div>
  );
}


// Blob-fetched preview for stored `emergent:` refs. Loads once per ref.
function useLogoPreview(serveUrl, ref) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!ref) { setSrc(null); return; }
    let alive = true, objectUrl = null;
    api.get(serveUrl, { params: { ref }, responseType: "blob" })
      .then(({ data }) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(data);
        setSrc(objectUrl);
      })
      .catch(() => { if (alive) setSrc(null); });
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [serveUrl, ref]);
  return src;
}


// ═════════════════════════════════════════════════════════════════════
// ORG-LEVEL branding panel — set defaults once, used by every project.
// Route: /settings/callsheet-branding
// ═════════════════════════════════════════════════════════════════════
export function OrgCallSheetBrandingPage() {
  const navigate = useNavigate();
  const [state, setState]     = useState(null);
  const [saving, setSaving]   = useState(false);
  const [uploading, setUploading] = useState(null);

  const reload = () =>
    api.get("/orgs/mine/callsheet-branding")
      .then(({ data }) => setState(data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)
                                    || "Could not load branding"));
  useEffect(() => { reload(); }, []);

  const primarySrc = useLogoPreview(
    "/orgs/mine/callsheet-branding/logo/serve",
    state?.branding?.primary_logo_ref);
  const slot2Src   = useLogoPreview(
    "/orgs/mine/callsheet-branding/logo/serve",
    state?.branding?.slot2_logo_ref);
  const slot3Src   = useLogoPreview(
    "/orgs/mine/callsheet-branding/logo/serve",
    state?.branding?.slot3_logo_ref);

  if (!state) {
    return <div className="min-h-screen bg-black text-white/60 flex items-center justify-center">Loading…</div>;
  }
  const b = state.branding;
  const canEdit = state.can_edit;

  const patchLabel = async (slot, value) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/orgs/mine/callsheet-branding",
        slot === "slot2" ? { slot2_label: value } : { slot3_label: value });
      setState((s) => ({ ...s, branding: data.branding }));
      toast.success("Label saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const uploadLogo = async (slot, file) => {
    setUploading(slot);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/orgs/mine/callsheet-branding/logo",
        fd, { params: { slot }, headers: { "Content-Type": "multipart/form-data" } });
      setState((s) => ({ ...s, branding: data.branding }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail)
                   || "Upload failed");
    } finally { setUploading(null); }
  };

  const clearLogo = async (slot) => {
    try {
      const { data } = await api.delete("/orgs/mine/callsheet-branding/logo",
        { params: { slot } });
      setState((s) => ({ ...s, branding: data.branding }));
      toast.success("Logo cleared");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Clear failed");
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="org-branding-page">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <button onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-white/60 hover:text-white mb-6"
                data-testid="org-branding-back">
          <ArrowLeft size={12} /> Back
        </button>
        <div className="flex items-start justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--association-theme)]">
              Call Sheet Branding
            </div>
            <h1 className="font-display text-4xl uppercase tracking-tight mt-2">
              Default Logos
            </h1>
            <p className="text-sm text-white/60 mt-2 max-w-2xl">
              These three logos are stamped on every call sheet PDF this
              organisation publishes. Missing logos are replaced by a
              placeholder text box showing the entity&apos;s name — you can
              upload the real marks any time.
            </p>
          </div>
        </div>

        {!canEdit && (
          <div className="mb-6 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-xs text-amber-200/90"
               data-testid="org-branding-readonly">
            Read-only view. Only the org owner or admin can edit branding.
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <LogoSlot
            title={<><Building2 size={11} className="inline mr-1"/> Primary</>}
            subtitle={state.organization_name || "Your production house / agency"}
            testidBase="org-branding-primary"
            ref={b.primary_logo_ref} previewSrc={primarySrc}
            disabled={!canEdit || saving}
            uploading={uploading === "primary"}
            onFilePicked={(f) => uploadLogo("primary", f)}
            onClear={() => clearLogo("primary")} />
          <LogoSlot
            title={<><Radio size={11} className="inline mr-1"/> Slot 2</>}
            subtitle={`Studio · Channel · Agency · Line Producer`}
            testidBase="org-branding-slot2"
            ref={b.slot2_logo_ref} previewSrc={slot2Src}
            labelOptions={SLOT2_LABELS}
            labelValue={b.slot2_label}
            onLabelChange={(v) => patchLabel("slot2", v)}
            disabled={!canEdit || saving}
            uploading={uploading === "slot2"}
            onFilePicked={(f) => uploadLogo("slot2", f)}
            onClear={() => clearLogo("slot2")} />
          <LogoSlot
            title={<><Handshake size={11} className="inline mr-1"/> Slot 3</>}
            subtitle={`Client · Partner`}
            testidBase="org-branding-slot3"
            ref={b.slot3_logo_ref} previewSrc={slot3Src}
            labelOptions={SLOT3_LABELS}
            labelValue={b.slot3_label}
            onLabelChange={(v) => patchLabel("slot3", v)}
            disabled={!canEdit || saving}
            uploading={uploading === "slot3"}
            onFilePicked={(f) => uploadLogo("slot3", f)}
            onClear={() => clearLogo("slot3")} />
        </div>

        <div className="mt-8 rounded-xl border border-white/10 bg-white/[0.03] p-5"
             data-testid="org-branding-note">
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 mb-2">
            Note · Project overrides
          </div>
          <p className="text-sm text-white/70 leading-relaxed">
            Slot 2 &amp; Slot 3 can be overridden per-project (e.g. this
            season is co-produced by a different partner). The project&apos;s
            title logo — the show / film / brand mark — is uploaded on
            the project&apos;s own branding page.
          </p>
        </div>
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// PROJECT-LEVEL branding panel — title logo + optional slot overrides.
// Route: /p/:id/branding
// ═════════════════════════════════════════════════════════════════════
export function ProjectCallSheetBrandingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState]     = useState(null);
  const [uploading, setUploading] = useState(null);
  const [saving, setSaving]   = useState(false);

  const reload = () =>
    api.get(`/projects/${id}/branding`)
      .then(({ data }) => setState(data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)
                                    || "Could not load branding"));
  useEffect(() => { reload(); }, [id]);

  // Preview refs from BOTH project overrides and org defaults
  const proj_b = state?.project_branding || {};
  const org_b  = state?.org_branding || {};
  const titleSrc  = useLogoPreview(`/projects/${id}/branding/logo/serve`,
                                    proj_b.title_logo_ref);
  const slot2ProjSrc = useLogoPreview(`/projects/${id}/branding/logo/serve`,
                                       proj_b.slot2_logo_ref);
  const slot3ProjSrc = useLogoPreview(`/projects/${id}/branding/logo/serve`,
                                       proj_b.slot3_logo_ref);
  const slot2OrgSrc  = useLogoPreview(`/projects/${id}/branding/logo/serve`,
                                       org_b.slot2_logo_ref);
  const slot3OrgSrc  = useLogoPreview(`/projects/${id}/branding/logo/serve`,
                                       org_b.slot3_logo_ref);

  if (!state) {
    return <div className="min-h-screen bg-black text-white/60 flex items-center justify-center">Loading…</div>;
  }

  const uploadLogo = async (slot, file) => {
    setUploading(slot);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/projects/${id}/branding/logo`,
        fd, { params: { slot }, headers: { "Content-Type": "multipart/form-data" } });
      setState((s) => ({ ...s, project_branding: data.branding }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(null); }
  };
  const clearLogo = async (slot) => {
    try {
      const { data } = await api.delete(`/projects/${id}/branding/logo`,
        { params: { slot } });
      setState((s) => ({ ...s, project_branding: data.branding }));
      toast.success("Logo cleared");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Clear failed");
    }
  };
  const patchLabel = async (field, value) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/projects/${id}/branding`,
        field === "slot2" ? { slot2_label: value } :
        field === "slot3" ? { slot3_label: value } :
                            { title_label: value });
      setState((s) => ({ ...s, project_branding: data.branding }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="project-branding-page">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <button onClick={() => navigate(`/p/${id}`)}
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-white/60 hover:text-white mb-6"
                data-testid="project-branding-back">
          <ArrowLeft size={12} /> Back to project
        </button>
        <div className="mb-8">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--association-theme)]">
            Call Sheet Branding
          </div>
          <h1 className="font-display text-4xl uppercase tracking-tight mt-2">
            {state.project_title || "Project"}
          </h1>
          <p className="text-sm text-white/60 mt-2 max-w-2xl">
            Upload the show / film / brand title mark. If needed, override
            the Slot 2 &amp; Slot 3 logos for just this project.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <LogoSlot
            title={<><Film size={11} className="inline mr-1"/> Title Logo</>}
            subtitle={proj_b.title_label || state.project_title}
            testidBase="project-branding-title"
            ref={proj_b.title_logo_ref} previewSrc={titleSrc}
            disabled={saving} uploading={uploading === "title"}
            onFilePicked={(f) => uploadLogo("title", f)}
            onClear={() => clearLogo("title")} />
          <LogoSlot
            title={<><Radio size={11} className="inline mr-1"/> Slot 2 override</>}
            subtitle={`Overrides "${org_b.slot2_label || "Co-Producer"}" from org`}
            testidBase="project-branding-slot2"
            ref={proj_b.slot2_logo_ref}
            previewSrc={slot2ProjSrc || (proj_b.slot2_logo_ref ? null : slot2OrgSrc)}
            labelOptions={SLOT2_LABELS}
            labelValue={proj_b.slot2_label || org_b.slot2_label}
            onLabelChange={(v) => patchLabel("slot2", v)}
            disabled={saving} uploading={uploading === "slot2"}
            onFilePicked={(f) => uploadLogo("slot2", f)}
            onClear={() => clearLogo("slot2")} />
          <LogoSlot
            title={<><Handshake size={11} className="inline mr-1"/> Slot 3 override</>}
            subtitle={`Overrides "${org_b.slot3_label || "Partner"}" from org`}
            testidBase="project-branding-slot3"
            ref={proj_b.slot3_logo_ref}
            previewSrc={slot3ProjSrc || (proj_b.slot3_logo_ref ? null : slot3OrgSrc)}
            labelOptions={SLOT3_LABELS}
            labelValue={proj_b.slot3_label || org_b.slot3_label}
            onLabelChange={(v) => patchLabel("slot3", v)}
            disabled={saving} uploading={uploading === "slot3"}
            onFilePicked={(f) => uploadLogo("slot3", f)}
            onClear={() => clearLogo("slot3")} />
        </div>
      </div>
    </div>
  );
}
